import React, { useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { Container } from "react-bootstrap";
import i_logo from "../../assets/img/id.express.svg";
import GlobalUserNavigation, {
  DEFAULT_IDEXPRESS_THEME,
  IdExpressApplicationLogo,
  NavigationThemeMode,
  themeTokensFromEnvironment,
  useIdExpressGlobalUserNavigation,
} from "@id-express/global-user-navigation";
import "./Layout.css";
import { Outlet, useLocation, matchPath, useNavigate } from "react-router-dom";
import Skeleton from "../Skeleton/Skeleton";
import { useTranslation } from "react-i18next";
import { LinkOrSkeletonProps } from "../../interfaces";
import { getFooterLinks } from './Layout.constants';
import useTimer from "../../hooks/useTimer";
import { AuthContext } from "../../contexts/AuthContext";
import { projectApiUrl } from "../../constants";
import { getIdApp, referer } from "../../utils";
import { getInitialLanguagePreference, persistLanguagePreference } from "../../languagePreference";
import {
  getInitialThemePreference,
  normalizeThemePreference,
  persistThemePreference,
  resolveThemePreference,
  ResolvedThemeMode,
  THEME_PREFERENCE_CHANGE_EVENT,
  ThemePreferenceMode,
} from "../../themePreference";
import { buildCentralLogoutUrl } from "../../authNavigation";
import { getAvatarDownloadUrl, PD_AVATAR_UPDATED_EVENT } from "../../storageUpload";

export type ThemeMode = ResolvedThemeMode;

export interface LayoutOutletContext {
  theme: ThemeMode;
  themePreference: ThemePreferenceMode;
  setThemePreference: (theme: ThemePreferenceMode) => void;
  toggleTheme: () => void;
  isMobileNavOpen: boolean;
  setIsMobileNavOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isPersonalDataRoute: boolean;
}

const DEFAULT_GUN_APP_ID = "a6729d6c-8ed9-4b63-81a8-6a0718247159";
const DEFAULT_GUN_AUTH_BASE_URL = "https://id.express";
const DEFAULT_GUN_DICTIONARY_API_BASE_URL = "https://qa-api-dictionary.id.express/v1/dictionaries";
const deploymentTheme = {...DEFAULT_IDEXPRESS_THEME, ...Object.fromEntries(Object.entries(themeTokensFromEnvironment(process.env)).filter(([, value]) => value))};
const deploymentThemeStyle = {
  "--theme-light-background": deploymentTheme.lightBackground,
  "--theme-light-surface": deploymentTheme.lightSurface,
  "--theme-light-text": deploymentTheme.lightText,
  "--theme-light-muted": deploymentTheme.lightMuted,
  "--theme-light-border": deploymentTheme.lightBorder,
  "--theme-dark-background": deploymentTheme.darkBackground,
  "--theme-dark-surface": deploymentTheme.darkSurface,
  "--theme-dark-text": deploymentTheme.darkText,
  "--theme-dark-muted": deploymentTheme.darkMuted,
  "--theme-dark-border": deploymentTheme.darkBorder,
  "--theme-accent": deploymentTheme.accent,
} as React.CSSProperties;

const Layout = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { AuthContextState, handleLogOut } = useContext(AuthContext);
  const currentLang = i18n.language;
  const currentYear = new Date().getFullYear();
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const isLinksLoaded = useTimer(500);
  const [themePreference, setThemePreferenceState] = useState<ThemePreferenceMode>(() => getInitialThemePreference());
  const [theme, setTheme] = useState<ThemeMode>(() => resolveThemePreference(getInitialThemePreference()));
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isAdministrator, setIsAdministrator] = useState(false);
  const [navigationAvatarUrl, setNavigationAvatarUrl] = useState<string | null>(null);
  const isPersonalDataRoute = Boolean(matchPath("/:lang/personal_data/*", location.pathname));
  const gunAppId = process.env.REACT_APP_GUN_APP_ID || getIdApp() || DEFAULT_GUN_APP_ID;
  const gunAuthBaseUrl = process.env.REACT_APP_GUN_AUTH_BASE_URL || DEFAULT_GUN_AUTH_BASE_URL;
  const gunDictionaryApiBaseUrl =
    process.env.REACT_APP_GUN_DICTIONARY_API_BASE_URL || DEFAULT_GUN_DICTIONARY_API_BASE_URL;

  // docs/security-and-platform-work-plan.md Этап 7 — the access token is an
  // HttpOnly cookie now, scoped to this app's own backend; api-admin.id.express
  // is a different host and can no longer receive it directly. This app's
  // own backend proxies the same check server-to-server (see
  // api-pd.id.express/backend/app/api/access_status.py).
  useEffect(() => {
    if (!AuthContextState.isAuthenticated || !projectApiUrl) {
      setIsAdministrator(false);
      return;
    }

    const controller = new AbortController();
    fetch(`${projectApiUrl.replace(/\/$/, "")}/access-status`, {
      headers: { "X-Requested-With": "XMLHttpRequest" },
      credentials: "include",
      signal: controller.signal,
    })
      .then((response) => response.ok ? response.json() : { isAdministrator: false })
      .then((payload) => setIsAdministrator(payload?.isAdministrator === true))
      .catch((error) => {
        if (error?.name !== "AbortError") setIsAdministrator(false);
      });

    return () => controller.abort();
  }, [AuthContextState.isAuthenticated]);

  useEffect(() => {
    if (!AuthContextState.isAuthenticated || !AuthContextState.user.userId || !projectApiUrl) {
      setNavigationAvatarUrl(null);
      return;
    }

    let cancelled = false;
    let generation = 0;
    let timer: number | undefined;
    const schedule = (delay: number, attempt: number, currentGeneration: number) => {
      timer = window.setTimeout(() => resolveAvatar(attempt, currentGeneration), delay);
    };
    const resolveAvatar = async (attempt: number, currentGeneration: number) => {
      try {
        const url = await getAvatarDownloadUrl();
        if (cancelled || currentGeneration !== generation) return;
        if (url) {
          setNavigationAvatarUrl(url);
          // Signed S3 URLs expire after five minutes; refresh before expiry.
          schedule(4 * 60 * 1000, 0, currentGeneration);
        } else if (attempt < 20) {
          schedule(3000, attempt + 1, currentGeneration);
        }
      } catch (error) {
        if (cancelled || currentGeneration !== generation) return;
        // A user without an avatar is normal. Other failures may be transient.
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          setNavigationAvatarUrl(null);
        } else {
          schedule(30000, 0, currentGeneration);
        }
      }
    };
    const onAvatarUpdated = (event: Event) => {
      generation += 1;
      if (timer !== undefined) window.clearTimeout(timer);
      const previewUrl = (event as CustomEvent<{ previewUrl?: string }>).detail?.previewUrl;
      setNavigationAvatarUrl(typeof previewUrl === "string" ? previewUrl : null);
      void resolveAvatar(0, generation);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      generation += 1;
      if (timer !== undefined) window.clearTimeout(timer);
      void resolveAvatar(0, generation);
    };

    window.addEventListener(PD_AVATAR_UPDATED_EVENT, onAvatarUpdated);
    document.addEventListener("visibilitychange", onVisibilityChange);
    void resolveAvatar(0, generation);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
      window.removeEventListener(PD_AVATAR_UPDATED_EVENT, onAvatarUpdated);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [AuthContextState.isAuthenticated, AuthContextState.user.userId]);

  const handleNavigationLanguageChange = (languageCode: string) => {
    languageCode = persistLanguagePreference(languageCode) || languageCode;
    i18n.changeLanguage(languageCode);
    if (typeof window === "undefined") return;

    const currentUrl = new URL(window.location.href);
    const path = currentUrl.pathname + currentUrl.search;
    const newPath = path.replace(/^\/([A-Za-z]{2})\//, (match, capturedLanguage) =>
      match.replace(capturedLanguage, languageCode)
    );

    navigate(newPath);
  };

  const setThemePreference = useCallback((selectedTheme: ThemePreferenceMode) => {
    const normalizedTheme = normalizeThemePreference(selectedTheme);
    if (!normalizedTheme) return;

    persistThemePreference(normalizedTheme);
    setThemePreferenceState(normalizedTheme);
    setTheme(resolveThemePreference(normalizedTheme));
  }, []);

  const handleNavigationThemeChange = (selectedTheme: NavigationThemeMode) => {
    const normalizedTheme = selectedTheme === "system" ? "auto" : selectedTheme;
    setThemePreference(normalizedTheme);
    window.dispatchEvent(new CustomEvent(THEME_PREFERENCE_CHANGE_EVENT, {
      detail: { themeMode: normalizedTheme },
    }));
  };

  const handleNavigationLogout = useCallback(async () => {
    // Consumer logout is authoritative for this UI and must not depend on the
    // central ID.Express session still existing.
    await handleLogOut({});
    window.location.assign(buildCentralLogoutUrl({
      authBaseUrl: gunAuthBaseUrl,
      appId: gunAppId,
      language: currentLang,
      consumerOrigin: window.location.origin,
    }));
  }, [currentLang, gunAppId, gunAuthBaseUrl, handleLogOut]);

  const { navigationConfig, navigationEventBus } = useIdExpressGlobalUserNavigation({
    isAuthenticated: AuthContextState.isAuthenticated,
    user: {
      email: AuthContextState.user.email,
      name: AuthContextState.user.name,
      avatarUrl: navigationAvatarUrl,
      isAdministrator,
    },
    currentLanguage: currentLang,
    currentTheme: theme,
    themeTokens: deploymentTheme,
    hiddenItems: ["wallet", "inbox"],
    appId: gunAppId,
    refererUrl: referer() || window.location.origin,
    authBaseUrl: gunAuthBaseUrl,
    dictionaryApiBaseUrl: gunDictionaryApiBaseUrl,
    onNavigate: (href) => {
      if (typeof window !== "undefined" && href.startsWith("http")) {
        window.location.href = href;
        return;
      }

      navigate(href);
    },
    onLanguageChange: handleNavigationLanguageChange,
    onThemeChange: handleNavigationThemeChange,
    onLogout: handleNavigationLogout,
    labels: {
      services: t("globalNav.services", { defaultValue: "Services" }),
      inbox: t("globalNav.inbox", { defaultValue: "Inbox" }),
      notifications: t("globalNav.notifications", { defaultValue: "Notifications" }),
      messages: t("globalNav.messages", { defaultValue: "Messages" }),
      wallet: t("globalNav.wallet", { defaultValue: "Wallet" }),
      quickActions: t("globalNav.quickActions", { defaultValue: "Quick actions" }),
      settings: t("globalNav.settings", { defaultValue: "Settings" }),
      profile: t("globalNav.profile", { defaultValue: "Profile" }),
      logout: t("globalNav.logout", { defaultValue: "Log out" }),
      manageSessions: t("globalNav.manageSessions", { defaultValue: "Manage sessions" }),
      language: t("globalNav.language", { defaultValue: "Language" }),
      theme: t("globalNav.theme", { defaultValue: "Theme" }),
      lightMode: t("globalNav.lightMode", { defaultValue: "Light" }),
      darkMode: t("globalNav.darkMode", { defaultValue: "Dark" }),
      systemMode: t("personalData.values.theme.auto", { defaultValue: "By time of day" }),
      favorites: t("globalNav.favorites", { defaultValue: "Pinned" }),
      availableServices: t("globalNav.availableServices", { defaultValue: "Available services" }),
      noUnread: t("globalNav.noUnread", { defaultValue: "You're all caught up" }),
      openLauncher: t("globalNav.openLauncher", { defaultValue: "Open launcher" }),
      close: t("globalNav.close", { defaultValue: "Close" }),
      online: t("globalNav.online", { defaultValue: "Online" }),
      offline: t("globalNav.offline", { defaultValue: "Offline" }),
      poorConnection: t("globalNav.poorConnection", { defaultValue: "Poor connection" }),
      accountMenu: t("globalNav.accountMenu", { defaultValue: "Account menu" }),
      openNavigation: t("globalNav.openNavigation", { defaultValue: "Open navigation" }),
    },
  });

  const handleImageLoad = () => {
    setIsImageLoaded(true);
  };

  useEffect(() => {
    setTheme(resolveThemePreference(themePreference));

    if (themePreference !== "auto") return;

    const intervalId = window.setInterval(() => {
      setTheme(resolveThemePreference("auto"));
    }, 5 * 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, [themePreference]);

  useEffect(() => {
    const preferredLanguage = getInitialLanguagePreference();
    if (!preferredLanguage || preferredLanguage === currentLang) return;

    i18n.changeLanguage(preferredLanguage);

    const currentUrl = new URL(window.location.href);
    if (!/^\/[A-Za-z]{2}\//.test(currentUrl.pathname)) return;

    const nextPath = currentUrl.pathname.replace(/^\/([A-Za-z]{2})\//, (match, capturedLanguage) =>
      match.replace(capturedLanguage, preferredLanguage)
    );

    navigate(`${nextPath}${currentUrl.search}`, { replace: true });
  }, [currentLang, i18n, navigate]);

  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileNavOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const renderLinkOrSkeleton = (props: LinkOrSkeletonProps): JSX.Element => {
    const { isLoaded, href, text, width } = props;
    return isLoaded ? (
      <a href={href}>{text}</a>
    ) : (
      <Skeleton width={width} height="20px" animation="enabled" display="inline" />
    );
  };

  const toggleTheme = () => {
    setThemePreference(theme === "dark" ? "light" : "dark");
  };

  return (
    <div className={`auth-background theme-${theme}`} style={deploymentThemeStyle}>
      <Container>
        <header className={`logoext app-header ${isPersonalDataRoute ? `app-header--${theme}` : ""}`}>
          <a
            className="app-header__brand"
            href={`/${currentLang}/personal_data?section=profile`}
            title={t("personalData.categories.profile", { defaultValue: "Profile" })}
            aria-label={t("personalData.categories.profile", { defaultValue: "Profile" })}
          >
            {!isImageLoaded && <Skeleton width="180px" height="53px" animation="enabled" />}
            <img
              src={i_logo}
              alt="id.Express"
              onLoad={handleImageLoad}
              style={{ display: !isImageLoaded ? "none" : undefined }}
            />
          </a>

          <div className="app-header__controls">
            <GlobalUserNavigation
              config={navigationConfig}
              eventBus={navigationEventBus}
              className="app-header__global-nav"
            />

            {isPersonalDataRoute && (
              <>
                <button
                  type="button"
                  className={`app-header__icon-button app-header__menu-button ${isMobileNavOpen ? "is-active" : ""}`}
                  onClick={() => setIsMobileNavOpen((current) => !current)}
                  aria-label={isMobileNavOpen ? t("personalData.ui.closeMenu", { defaultValue: "Close menu" }) : t("personalData.ui.openMenu", { defaultValue: "Open menu" })}
                  aria-expanded={isMobileNavOpen}
                  aria-controls="pd-mobile-nav"
                >
                  <span />
                  <span />
                  <span />
                </button>
              </>
            )}
          </div>
        </header>
        <div className="justify-content-center align-items-center d-flex flex-column">
          <div className="personaldata-card">
            <Outlet
              context={
                {
                  theme,
                  themePreference,
                  setThemePreference,
                  toggleTheme,
                  isMobileNavOpen,
                  setIsMobileNavOpen,
                  isPersonalDataRoute,
                } satisfies LayoutOutletContext
              }
            />
          </div>
          <footer className="py-3 my-4">
            <ul className="nav justify-content-center pb-3 mb-3 pd-footer-services">
              {navigationConfig.services.filter((service) => service.href).map((service) => (
                <li className="nav-item" key={service.id}>
                  <a
                    href={service.href}
                    className="nav-link pd-footer-service-link"
                    title={service.name}
                    aria-label={service.name}
                  >
                    <span className="pd-footer-service-icon">
                      {service.icon ?? <IdExpressApplicationLogo name={service.name} />}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
            <p className="text-center footer-links">
              {getFooterLinks(isLinksLoaded, currentYear, currentLang, t).map((linkProps, index) => (
                <span key={linkProps.href}>
                  {renderLinkOrSkeleton(linkProps)}
                </span>
              ))}
            </p>
          </footer>
        </div>
      </Container>
    </div>
  );
};

export default Layout;
